import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { SubmarketComparisonData } from '../components/PropertyView/SubmarketComparisonTable';

/**
 * Response from GET /api/zandi/submarkets endpoint
 */
interface SubmarketListResponse {
    submarkets: Array<{
        id: number;
        name: string;
        property_type: string;
        state: string;
        latest_index: number | null;
        latest_egi: number | null;
        five_year_growth_pct: number | null;
        cagr_5yr: number | null;
        neighborhood_count: number;
    }>;
    total: number;
    property_type: string;
}

/**
 * Response from GET /api/submarket-scores endpoint (if available)
 */
interface SubmarketScoresResponse {
    scores: Array<{
        submarket_id: number;
        name: string;
        composite_score: number;
        rent_gap_score: number;
        mobi_score: number;
        momentum_score: number;
        vacancy_score: number;
        valuation_score: number;
        permit_score: number;
        spillover_score: number;
        rank: number;
    }>;
    total: number;
    metro_egi: number;
    metro_cagr: number;
    computed_at: string;
}

/**
 * Hook to fetch submarket comparison data for the SubmarketComparisonTable.
 *
 * Strategy:
 * 1. First try to fetch from /api/submarket-scores (full scoring data)
 * 2. If that fails, derive scores from zandi_scoring data on the property
 * 3. Falls back to empty array if no data available
 *
 * @param currentSubmarket - The name of the property's submarket
 * @param zandiScoring - Optional zandi_scoring data from PropertyAnalysisResult
 */
export function useSubmarketComparison(
    currentSubmarket: string,
    zandiScoring?: {
        name: string;
        rank?: number;
        composite_score: number;
        rent_gap_score: number;
        momentum_score: number;
        raw_data?: {
            submarket_egi?: number;
            metro_egi?: number;
        };
    }
) {
    return useQuery<SubmarketComparisonData[]>({
        queryKey: ['submarket-comparison', currentSubmarket],
        queryFn: async () => {
            try {
                // Try to fetch full scoring data from submarkets/scores endpoint
                const { data } = await api.get<SubmarketScoresResponse>('/submarkets/scores');

                // API returns 'submarkets' key, not 'scores' — handle both
                const scores = data.scores ?? (data as unknown as { submarkets: SubmarketScoresResponse['scores'] }).submarkets ?? [];

                // Map to SubmarketComparisonData format
                // For the current submarket, use zandiScoring values to ensure consistency
                // with the header display (fixes 56-09 score mismatch)
                return scores.map((score) => {
                    const isCurrentSubmarket = zandiScoring &&
                        score.name.toLowerCase().trim() === zandiScoring.name.toLowerCase().trim();

                    if (isCurrentSubmarket) {
                        return {
                            name: score.name,
                            rank: zandiScoring.rank || score.rank,
                            compositeScore: zandiScoring.composite_score,
                            rentGapScore: zandiScoring.rent_gap_score,
                            momentumScore: zandiScoring.momentum_score,
                        };
                    }

                    return {
                        name: score.name,
                        rank: score.rank,
                        compositeScore: score.composite_score,
                        rentGapScore: score.rent_gap_score,
                        momentumScore: score.momentum_score,
                    };
                });
            } catch {
                // Submarket scores endpoint may not exist; try zandi submarkets
                try {
                    const { data: zandiData } = await api.get<SubmarketListResponse>('/zandi/submarkets');

                    // Derive basic comparison data from Zandi metrics
                    // Note: This is a fallback - doesn't have full scoring data
                    // CAGR comes as percentage (e.g., 11.7 for 11.7%), need to scale appropriately
                    // TODO: Replace with market-specific metro EGI values from backend or market store
                    const METRO_EGI = 1553; // Philadelphia metro average EGI

                    const submarkets: SubmarketComparisonData[] = zandiData.submarkets
                        .filter((s) => s.cagr_5yr !== null)
                        .map((submarket, index) => {
                            // If this is the current submarket and we have actual scoring data,
                            // use those scores instead of recalculating (fixes 56-09 score mismatch)
                            const isCurrentSubmarket = zandiScoring &&
                                submarket.name.toLowerCase().trim() === zandiScoring.name.toLowerCase().trim();
                            if (isCurrentSubmarket) {
                                return {
                                    name: submarket.name,
                                    rank: zandiScoring.rank || index + 1,
                                    compositeScore: zandiScoring.composite_score,
                                    rentGapScore: zandiScoring.rent_gap_score,
                                    momentumScore: zandiScoring.momentum_score,
                                };
                            }

                            // CAGR is in percentage form (e.g., 11.7 for 11.7%)
                            const cagr = submarket.cagr_5yr || 0;

                            // Rent gap score: how much below metro (positive gap = opportunity)
                            // Score 50 = at parity, 100 = 50% below metro, 0 = 50% above metro
                            const gapPct = submarket.latest_egi
                                ? ((METRO_EGI - submarket.latest_egi) / METRO_EGI) * 100
                                : 0;
                            const rentGapScore = Math.min(100, Math.max(0, 50 + gapPct));

                            // Momentum score: based on CAGR (5-year compound growth)
                            // Score 50 = 0% CAGR, 100 = 10%+ CAGR, 0 = -10% CAGR
                            const momentumScore = Math.min(100, Math.max(0, 50 + (cagr * 5)));

                            // Composite: weighted average (rent gap 40%, momentum 60%)
                            const compositeScore = (rentGapScore * 0.4) + (momentumScore * 0.6);

                            return {
                                name: submarket.name,
                                rank: index + 1, // Will be overwritten after sorting
                                compositeScore,
                                rentGapScore,
                                momentumScore,
                            };
                        })
                        .sort((a, b) => b.compositeScore - a.compositeScore)
                        .map((s, index) => ({ ...s, rank: index + 1 }));

                    return submarkets;
                } catch {
                    // All API calls failed - return fallback with just current submarket
                    if (zandiScoring) {
                        return [
                            {
                                name: zandiScoring.name,
                                rank: zandiScoring.rank || 1,
                                compositeScore: zandiScoring.composite_score,
                                rentGapScore: zandiScoring.rent_gap_score,
                                momentumScore: zandiScoring.momentum_score,
                            },
                        ];
                    }
                    return [];
                }
            }
        },
        enabled: !!currentSubmarket,
        staleTime: 10 * 60 * 1000, // 10 minutes - submarket data doesn't change often
        retry: 1,
    });
}

export default useSubmarketComparison;
